/**
 * Copy for "this action won't apply" — the clause that names what is missing
 * and, when the user can fix it, WHERE: the number of the create action they
 * unchecked, as printed on its card ("2. Create new contact: …").
 *
 * The clause is prefix-free so each surface can lead with its own verb:
 * a row's checkbox tooltip says "Won't apply – …", an absorbed link's chip
 * says "Won't be linked – …".
 */

export interface MissingEntityDescription {
  /**
   * How the entity is named on screen ("Acme Robotics"), or `null` when
   * nothing names it. A numbered reference then cites the number alone
   * ("action 3 is unchecked") rather than a placeholder in brackets.
   */
  name: string | null;
  /**
   * The printed number of the create action that would bring it into
   * existence, when that action is on this proposal AND the list is numbered
   * (a single-card proposal shows no numbers, so none is cited).
   */
  displayNumber: number | undefined;
  /**
   * Whether this proposal creates the entity. If it does, it is missing
   * because that create is unchecked; if it doesn't, the live record is gone.
   */
  createdInProposal: boolean;
}

/** "a", "a and b", "a, b, and c" — Oxford comma. */
function joinNames(parts: readonly string[]): string {
  if (parts.length <= 1) {
    return parts[0] ?? '';
  }
  if (parts.length === 2) {
    return `${parts[0]} and ${parts[1]}`;
  }
  return `${parts.slice(0, -1).join(', ')}, and ${parts.at(-1)}`;
}

/** What to call an entity nothing names, where a name would otherwise go. */
const UNNAMED = 'a record it needs';

function describeUnchecked(
  missing: readonly MissingEntityDescription[]
): string {
  const numbered = missing.every(m => m.displayNumber !== undefined);
  if (numbered) {
    // In the order the cards appear, not the order the action references them
    // (a link names its source before its target, which reads "2 and 1").
    const refs = [...missing]
      .sort((a, b) => (a.displayNumber ?? 0) - (b.displayNumber ?? 0))
      .map(m =>
        m.name === null
          ? `${m.displayNumber}`
          : `${m.displayNumber} (${m.name})`
      );
    return missing.length === 1
      ? `action ${refs[0]} is unchecked`
      : `actions ${joinNames(refs)} are unchecked`;
  }
  const names = missing.map(m => m.name ?? UNNAMED);
  return missing.length === 1
    ? `${names[0]} is unchecked`
    : `${joinNames(names)} are unchecked`;
}

function describeGone(missing: readonly MissingEntityDescription[]): string {
  const names = missing.map(m => m.name ?? UNNAMED);
  return missing.length === 1
    ? `${names[0]} no longer exists`
    : `${joinNames(names)} no longer exist`;
}

/**
 * The clause explaining why an action can't apply, for the entities it
 * references that won't exist. Variants:
 *
 *   action 3 (Acme Robotics) is unchecked
 *   actions 2 (Priya Natarajan) and 3 (Acme Robotics) are unchecked
 *   action 3 is unchecked                          (nothing names the entity)
 *   Acme Robotics is unchecked                     (list isn't numbered)
 *   a record it needs is unchecked                 (unnumbered and unnamed)
 *   Acme Robotics no longer exists                 (deleted live record)
 *   action 3 (Acme Robotics) is unchecked and Old Co no longer exists
 */
export function describeMissingEntities(
  missing: readonly MissingEntityDescription[]
): string {
  const unchecked = missing.filter(m => m.createdInProposal);
  const gone = missing.filter(m => !m.createdInProposal);
  const clauses: string[] = [];
  if (unchecked.length > 0) {
    clauses.push(describeUnchecked(unchecked));
  }
  if (gone.length > 0) {
    clauses.push(describeGone(gone));
  }
  return clauses.join(' and ');
}
